import { Link } from 'react-router-dom';
import { ChevronRight, History } from 'lucide-react';

import type { DeliveryJob } from '@/api/types';
import { Main, PageHeader } from '@/components/app-shell';
import { EmptyState, QueryBoundary } from '@/components/states';
import { StatusPill } from '@/components/status-pill';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useHistory } from '@/hooks/use-deliveries';
import { formatDayLabel, formatMoney, formatTime, plural } from '@/lib/format';
import { isCashOnDelivery } from '@/lib/status';

/**
 * What this rider has finished — delivered and not delivered together.
 *
 * Both, deliberately. A list of successes is a scoreboard; a rider looking
 * back is usually trying to find the one that went wrong — which address it
 * was, what they wrote as the reason — and hiding failures makes the only
 * useful query impossible.
 *
 * Grouped by day, because "today" and "yesterday" is the resolution a shift is
 * actually remembered in.
 */
export function HistoryScreen() {
  const query = useHistory();

  return (
    <>
      <PageHeader title="History" />
      <Main className="pt-4">
        <QueryBoundary
          query={query}
          skeleton={
            <div className="grid gap-3">
              <Skeleton className="h-6 w-24" />
              <Skeleton className="h-20" />
              <Skeleton className="h-20" />
              <Skeleton className="h-20" />
            </div>
          }
        >
          {(jobs) =>
            jobs.length === 0 ? (
              <div className="flex min-h-[60vh] flex-col">
                <EmptyState
                  icon={History}
                  title="Nothing finished yet"
                  description="Deliveries you complete — and the ones you cannot — show up here, newest first."
                />
              </div>
            ) : (
              <HistoryList jobs={jobs} />
            )
          }
        </QueryBoundary>
      </Main>
    </>
  );
}

function HistoryList({ jobs }: { jobs: DeliveryJob[] }) {
  // Grouped at render rather than in the hook: it is a presentation decision,
  // and the hook's job is to say what happened, not how it should be stacked.
  const groups = new Map<string, DeliveryJob[]>();
  for (const job of jobs) {
    const day = formatDayLabel(job.deliveredAt ?? job.createdAt);
    const bucket = groups.get(day);
    if (bucket) bucket.push(job);
    else groups.set(day, [job]);
  }

  return (
    <div className="grid gap-6">
      {[...groups.entries()].map(([day, dayJobs]) => (
        <section key={day} aria-label={day}>
          <div className="flex items-baseline justify-between gap-3 pb-2">
            <h2 className="eyebrow">{day}</h2>
            <span data-numeric className="text-xs text-ink-subtle">
              {plural(dayJobs.length, 'job')}
            </span>
          </div>

          <ul className="grid gap-2">
            {dayJobs.map((job) => (
              <li key={job.id}>
                <HistoryRow job={job} />
              </li>
            ))}
          </ul>
        </section>
      ))}

      <p className="border-t border-line pt-5 text-center text-sm text-ink-subtle">
        Your last {jobs.length} finished jobs. Older ones are with the office.
      </p>
    </div>
  );
}

function HistoryRow({ job }: { job: DeliveryJob }) {
  const finishedAt = job.deliveredAt ?? job.createdAt;

  return (
    <Card asChild interactive>
      <Link
        to={`/jobs/${job.id}`}
        className="flex items-center gap-3 p-3.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40"
      >
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline justify-between gap-2">
            <p className="truncate font-semibold text-ink">{job.orderNumber ?? 'Order'}</p>
            <time data-numeric dateTime={finishedAt} className="shrink-0 text-xs text-ink-subtle">
              {formatTime(finishedAt)}
            </time>
          </div>

          <p className="mt-0.5 truncate text-sm text-ink-muted">
            {job.customerName ?? 'Customer'}
            {job.pharmacyName ? ` · ${job.pharmacyName}` : ''}
          </p>

          <div className="mt-2 flex flex-wrap items-center gap-2">
            <StatusPill status={job.status} />
            {/*
             * The one differentiator on a long list of otherwise identical
             * rows. Cash is also what a rider is reconciling against at the
             * end of a shift, which is the main reason to open this screen.
             */}
            {job.status === 'DELIVERED' && isCashOnDelivery(job) ? (
              <span data-numeric className="text-xs font-semibold text-ink-muted">
                {formatMoney(job.codAmount)} cash
              </span>
            ) : null}
          </div>

          {job.failureReason ? (
            <p className="mt-1.5 text-sm leading-snug text-ink-subtle [overflow-wrap:anywhere]">
              {job.failureReason}
            </p>
          ) : null}
        </div>

        <ChevronRight className="size-5 shrink-0 text-ink-subtle" aria-hidden="true" />
      </Link>
    </Card>
  );
}
